import classNames from 'classnames';
import moment from 'moment';
import React from 'react';
import _ from 'underscore';

import {trackEvent} from 'analytics/Analytics';
import {ACTION_CATEGORY, ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import BenchlingConfig from 'BenchlingConfig';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FileBrowserUtil from 'file-browser/FileBrowserUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as nyc from 'ui-library/NYC';
import fuzzydate from 'util/fuzzydate';

const MAX_FILES = BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS ? 10 : 20;

/** @package */
export interface FileSummaryFile {
  endAccess?: string;
  id: string;
  name: string;
}

export interface FileSummaryProps {
  emptyText: string;
  files: Array<FileSummaryFile>;
  headerText: string;
  activeItemId?: string | null;
}

export default function FileSummary(props: FileSummaryProps): JSX.Element {
  const _openFile = (file: FileSummaryFile): void => {
    FileBrowserActions.openFile(file.id);
  };

  // Sort files by last modified and only render up to MAX_FILES files.
  const files = _.sortBy(props.files, (file) => moment(file.endAccess))
    .reverse()
    .slice(0, MAX_FILES);
  return (
    <nyc.Container size="sidepanel" className="homeSidepanel-today-itemSummary homeSidepanel-file-summary">
      <nyc.Row className="homeSidepanel-today-sectionHeader">
        <div className="homeSidepanel-today-sectionHeader-content">{props.headerText}</div>
      </nyc.Row>
      {(() => {
        if (props.files.length === 0) {
          return (
            <nyc.Row className="homeSidepanel-today-item">
              <div {...getTestProps({component: 'FileSummary', element: 'noRecentEntityRow'})}>
                {props.emptyText}
              </div>
            </nyc.Row>
          );
        }
        return files.map((file, i) => {
          const handleFileClick = (): void => {
            trackEvent({
              module: ANALYTICS_MODULE.NAVIGATION,
              actionCategory: ACTION_CATEGORY.READ,
              action: 'file_summary_file_clicked',
            });
            _openFile(file);
          };
          let dateString;
          if (!file.endAccess) {
            dateString = null;
          } else {
            dateString = fuzzydate(file.endAccess, {showYear: false, showWeekday: true, compact: true});
          }
          return (
            <nyc.Row
              className={classNames('homeSidepanel-today-item', {
                'homeSidepanel-today-item--active': file.id === props.activeItemId,
              })}
              dataTestAttrs={{component: 'FileSummary', element: 'todayItem', key: file.id}}
              key={i}
            >
              <div
                className="homeSidepanel-today-item-content u-pointer u-flex-display"
                onClick={handleFileClick}
              >
                <div className="homeSidepanel-today-item-icon">
                  <BenchlingIcon icon={FileBrowserUtil.getFileIconName(file)} />
                </div>
                <div className="homeSidepanel-today-item-title whiteLink">{file.name}</div>
                <div className="homeSidepanel-today-item-subtitle u-small-spacing-left">{dateString}</div>
              </div>
            </nyc.Row>
          );
        });
      })()}
    </nyc.Container>
  );
}
